// The room's displays, as the build sees them.
//
// A display is one `doc.media` entry: the TV on the wall, or a projector with
// the screen it throws at. The catalogue model of each half rides on that
// entry (`productId`, `screen.productId`), so the TV in the parts list and the
// TV the solver sees are the same object. This module is the product side of
// that: which catalogue types are displays, what binding a model does to an
// entry, and moving the displays saved as gear (before an entry could carry a
// model) into media.

import {
  DEFAULT_DIAGONAL_IN, inchesToM, mToInches,
  type EditorDoc, type GearEntry, type MediaAspect, type MediaEntry, type MediaKind, type MediaScreen,
} from '@roomtreatment/core';
import type { Product } from '@roomtreatment/av-catalog';

/** The half of a media entry a model is: the device (a TV, or the projector
 *  itself) or a projector setup's screen. */
export type MediaPart = 'device' | 'screen';

export interface DisplayRole {
  /** The kind of media entry the model belongs to. */
  kind: MediaKind;
  part: MediaPart;
}

const ROLES: Readonly<Record<string, DisplayRole>> = {
  tv: { kind: 'tv', part: 'device' },
  projector: { kind: 'projector', part: 'device' },
  projector_screen: { kind: 'projector', part: 'screen' },
};

/** Where a catalogue display goes in a media entry; null for anything else. */
export function displayRole(product: Pick<Product, 'type'>): DisplayRole | null {
  return ROLES[product.type as string] ?? null;
}

/** The model bound to one half of an entry, or null for a generic one. */
export function mediaProductId(entry: MediaEntry, part: MediaPart): string | null {
  return (part === 'device' ? entry.productId : entry.screen?.productId) ?? null;
}

/** One half of a display, named without a model: `65" TV`, `Projector`,
 *  `120" screen` — the Media rail's own wording. */
export function genericDisplayName(entry: Pick<MediaEntry, 'kind' | 'diagonalM'>, part: MediaPart): string {
  const inches = Math.round(mToInches(entry.diagonalM));
  if (part === 'screen') return `${inches}" screen`;
  return entry.kind === 'tv' ? `${inches}" TV` : 'Projector';
}

export interface MediaPicture {
  diagonalM: number;
  aspect: MediaAspect;
}

/** The picture a model publishes: a TV's panel, a screen's surface. A
 *  projector publishes none; its picture is its screen's. */
export function publishedPicture(product: Product): MediaPicture | null {
  const s = product.specs;
  if (s.kind === 'tv' && s.screenIn) return { diagonalM: inchesToM(s.screenIn.v), aspect: '16:9' };
  if (s.kind === 'projector_screen' && s.diagonalIn && s.aspect) {
    return { diagonalM: inchesToM(s.diagonalIn.v), aspect: s.aspect.v };
  }
  return null;
}

/** A new display's picture: the model's own, else where that kind starts. */
export function startingPicture(kind: MediaKind, product?: Product): MediaPicture {
  return (product && publishedPicture(product)) ?? { diagonalM: inchesToM(DEFAULT_DIAGONAL_IN[kind]), aspect: '16:9' };
}

/**
 * What binding `product` to `entry` does, as an UPDATE_MEDIA patch: the model
 * on its half, and the picture it publishes, so a 77" model is a 77" TV. A
 * screen model also says whether sound passes through it. Null when the model
 * is not for this kind of entry, or is a screen for a projector that throws at
 * nothing and no `screen` placement was given to hang it at.
 */
export function mediaBinding(
  entry: Pick<MediaEntry, 'kind' | 'screen'>,
  product: Product,
  screen?: MediaScreen,
): Partial<Omit<MediaEntry, 'id'>> | null {
  const role = displayRole(product);
  if (!role || role.kind !== entry.kind) return null;
  const picture = publishedPicture(product) ?? {};
  if (role.part === 'device') return { ...picture, productId: product.id };

  const place = entry.screen ?? screen;
  if (!place || product.specs.kind !== 'projector_screen') return null;
  const transparent = product.specs.acousticallyTransparent?.v;
  return {
    ...picture,
    screen: {
      ...place,
      productId: product.id,
      ...(transparent === undefined ? {} : { acousticallyTransparent: transparent }),
    },
  };
}

/** A display placed in a room by the editor's own defaults (panel-surface's
 *  `defaultTvOn` / `defaultProjector`, which av-compat does not depend on):
 *  null when the room has nowhere to put one. */
export type PlaceDisplay = (doc: EditorDoc, kind: MediaKind, picture: MediaPicture) => Omit<MediaEntry, 'id'> | null;

/**
 * Displays saved as gear (a TV, projector or screen model added before a media
 * entry could carry one), moved into media. Each binds to the room's own
 * display of its kind when that half has no model yet; otherwise it becomes a
 * display of its own, placed by `place`. Devices move before screens, so a
 * gear projector and a gear screen end up one setup.
 *
 * Returns null when nothing moved. A gear id the catalogue does not know, or a
 * display the room has nowhere to put, stays in gear — so a caller that runs
 * this whenever the document changes cannot loop.
 */
export function adoptGearDisplays(
  doc: EditorDoc,
  catalog: ReadonlyMap<string, Product>,
  place: PlaceDisplay,
  newId: () => string,
): { gear: GearEntry[]; media: MediaEntry[] } | null {
  const gear = doc.gear ?? [];
  const displays = gear
    .flatMap((entry) => {
      const product = catalog.get(entry.productId);
      const role = product ? displayRole(product) : null;
      return product && role ? [{ entry, product, role }] : [];
    })
    .sort((a, b) => Number(a.role.part === 'screen') - Number(b.role.part === 'screen'));
  if (displays.length === 0) return null;

  let media = [...(doc.media ?? [])];
  const moved = new Set<GearEntry>();
  const current = (): EditorDoc => ({ ...doc, gear: gear.filter((g) => !moved.has(g)), media });

  for (const { entry, product, role } of displays) {
    const target = media.find((m) => m.kind === role.kind && mediaProductId(m, role.part) === null);
    if (target) {
      const hang = role.part === 'screen' && !target.screen
        ? place(current(), 'projector', startingPicture('projector', product))?.screen
        : undefined;
      const patch = mediaBinding(target, product, hang);
      if (patch) {
        media = media.map((m) => (m === target ? { ...m, ...patch } : m));
        moved.add(entry);
        continue;
      }
    }
    const placed = place(current(), role.kind, startingPicture(role.kind, product));
    const created: MediaEntry | null = placed ? { ...placed, id: newId() } : null;
    const patch = created ? mediaBinding(created, product) : null;
    if (!created || !patch) continue;
    media = [...media, { ...created, ...patch }];
    moved.add(entry);
  }

  if (moved.size === 0) return null;
  return { gear: gear.filter((g) => !moved.has(g)), media };
}
